import { useState, useEffect, useRef } from "react";
import { MapPin, X, ChevronDown } from "lucide-react";
import { api } from "@/api/client";

export default function PostageStatementAddressModal({ onClose, onSave, existingAddress }) {
  const [name, setName] = useState(existingAddress?.name || "");
  const [address, setAddress] = useState(existingAddress?.address || "");
  const [city, setCity] = useState(existingAddress?.city || "");
  const [state, setState] = useState(existingAddress?.state || "");
  const [zip, setZip] = useState(existingAddress?.zip || "");
  const [error, setError] = useState("");
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [filteredAddresses, setFilteredAddresses] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const addressInputRef = useRef(null);
  const nameInputRef = useRef(null);

  /**
   * Street address autocomplete.
   *
   * The Base44 version loaded the Google Maps SDK into the page with the API
   * key in the script URL, which puts a billable key in front of anyone who
   * opens the app. The key stays on the server here, so this asks the server
   * instead - the same lookup the destination search uses.
   *
   * The port kept the label promising autocomplete and implemented none of it,
   * so the field has been a plain text box the whole time.
   */
  const [addressMatches, setAddressMatches] = useState([]);
  const [lookingUp, setLookingUp] = useState(false);
  const [showAddressMatches, setShowAddressMatches] = useState(false);
  const lookupTimer = useRef(null);

  useEffect(() => {
    const query = address.trim();

    if (lookupTimer.current) clearTimeout(lookupTimer.current);

    // Short strings match half of Indiana and cost a call each.
    if (query.length < 5) {
      setAddressMatches([]);
      return undefined;
    }

    // Typing is faster than the round trip, so wait for a pause rather than
    // firing a lookup per keystroke.
    lookupTimer.current = setTimeout(async () => {
      setLookingUp(true);
      try {
        const { candidates } = await api.extraction.searchAddresses(query);
        setAddressMatches(candidates ?? []);
        setShowAddressMatches((candidates ?? []).length > 0);
      } catch {
        // Autocomplete is a convenience. Losing it must not stop someone
        // typing the address out.
        setAddressMatches([]);
      } finally {
        setLookingUp(false);
      }
    }, 400);

    return () => {
      if (lookupTimer.current) clearTimeout(lookupTimer.current);
    };
  }, [address]);

  const handleSelectMatch = (match) => {
    setAddress(match.address_line1 || address);
    if (match.city) setCity(match.city);
    if (match.state) setState(match.state);
    if (match.zip) setZip(match.zip);
    setShowAddressMatches(false);
  };


  /**
   * The reusable "mailed for" address book.
   *
   * Its own table now. It used to be a JSON array inside the settings row,
   * updated with a read-modify-write from the browser — two operators saving at
   * the same moment silently lost one of the addresses.
   */
  const loadSavedAddresses = async () => {
    try {
      setSavedAddresses(await api.entities.PostageStatementAddress.list());
    } catch (err) {
      console.error("Failed to load saved addresses:", err);
    }
  };

  useEffect(() => {
    loadSavedAddresses();
  }, []);

  useEffect(() => {
    if (name.trim().length > 0) {
      const filtered = savedAddresses.filter(addr => 
        addr.name.toLowerCase().includes(name.toLowerCase())
      );
      setFilteredAddresses(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredAddresses([]);
      setShowSuggestions(false);
    }
  }, [name, savedAddresses]);

  const handleSelectSavedAddress = (savedAddr) => {
    setName(savedAddr.name);
    setAddress(savedAddr.address);
    setCity(savedAddr.city);
    setState(savedAddr.state);
    setZip(savedAddr.zip);
    setShowSuggestions(false);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Please enter a name");
      return;
    }
    if (!address.trim()) {
      setError("Please enter an address");
      return;
    }
    if (!city.trim()) {
      setError("Please enter a city");
      return;
    }
    if (!state.trim()) {
      setError("Please enter a state");
      return;
    }
    if (!zip.trim()) {
      setError("Please enter a ZIP code");
      return;
    }

    const addressData = {
      name: name.trim(),
      address: address.trim(),
      city: city.trim(),
      state: state.trim(),
      zip: zip.trim()
    };

    // Setting it on the mailing is what stores it: the server finds or creates
    // the row and points the mailing at it, so it is in the book for next time
    // either way. This used to POST it here as well, which conflicted on the
    // unique key every time an address was reused - handled in code, but the
    // browser still logged a 409 for a request that never needed making.
    onSave(addressData);
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "1rem"
    }}>
      <div className="card" style={{ maxWidth: "700px", width: "100%", boxSizing: "border-box", overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600 }}>
            <MapPin size={20} style={{ display: "inline", marginRight: "0.5rem" }} />
            Postage Statement Address
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "0.25rem",
              color: "#64748b"
            }}
          >
            <X size={24} />
          </button>
        </div>

        <p style={{ fontSize: "0.875rem", color: "#64748b", marginBottom: "1.5rem" }}>
          This address will be overlayed on the Postage Statement PDF. Start typing a name to see saved addresses, or use address autocomplete.
        </p>

        {error && (
          <div style={{
            background: "#fef2f2",
            color: "#dc2626",
            padding: "0.75rem 1rem",
            borderRadius: "8px",
            marginBottom: "1rem",
            fontSize: "0.875rem"
          }}>
            {error}
          </div>
        )}

        {/* Name Field with Autocomplete Suggestions */}
        <div className="form-group" style={{ marginBottom: "1rem", position: "relative" }}>
          <label className="form-label" htmlFor="psa-name">Name *</label>
          <input
            id="psa-name"
            ref={nameInputRef}
            type="text"
            className="form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onFocus={() => {
              if (filteredAddresses.length > 0) {
                setShowSuggestions(true);
              }
            }}
            placeholder="e.g., John Doe"
          />
          
          {/* Saved Address Suggestions Dropdown */}
          {showSuggestions && filteredAddresses.length > 0 && (
            <div style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "white",
              border: "2px solid #3b82f6",
              borderRadius: "8px",
              marginTop: "0.25rem",
              maxHeight: "200px",
              overflowY: "auto",
              zIndex: 10,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
            }}>
              <div style={{
                padding: "0.5rem 0.75rem",
                background: "#eff6ff",
                borderBottom: "1px solid #bfdbfe",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "#1e40af"
              }}>
                Saved Addresses
              </div>
              {filteredAddresses.map((addr, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSelectSavedAddress(addr)}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    textAlign: "left",
                    background: "white",
                    border: "none",
                    borderBottom: idx < filteredAddresses.length - 1 ? "1px solid #e2e8f0" : "none",
                    cursor: "pointer",
                    transition: "background 0.2s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                >
                  <div style={{ fontWeight: 600, fontSize: "0.875rem", color: "#1e293b", marginBottom: "0.125rem" }}>
                    {addr.name}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                    {addr.address}, {addr.city}, {addr.state} {addr.zip}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="form-group" style={{ marginBottom: "1rem", position: "relative" }}>
          <label className="form-label" htmlFor="psa-street">
            Street Address * <span className="muted">(start typing to search)</span>
          </label>
          <input
            ref={addressInputRef}
            type="text"
            className="form-input"
            id="psa-street"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onFocus={() => addressMatches.length > 0 && setShowAddressMatches(true)}
            onBlur={() => setTimeout(() => setShowAddressMatches(false), 150)}
            placeholder="e.g., 123 Main Street"
            autoComplete="off"
          />

          {lookingUp && (
            <p className="field-hint">Looking up addresses...</p>
          )}

          {showAddressMatches && addressMatches.length > 0 && (
            <div className="address-matches">
              {addressMatches.map((match, index) => (
                <button
                  type="button"
                  key={`${match.address_line1}-${index}`}
                  className="address-match"
                  // onMouseDown, not onClick: the input's blur fires first and
                  // would close this before a click ever landed.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectMatch(match);
                  }}
                >
                  <span className="address-match-line">{match.address_line1}</span>
                  <span className="address-match-sub">
                    {[match.city, match.state, match.zip].filter(Boolean).join(', ')}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "1rem", marginBottom: "1rem", width: "100%", boxSizing: "border-box" }}>
          <div className="form-group" style={{ minWidth: 0, overflow: "hidden" }}>
            <label className="form-label" htmlFor="psa-city">City *</label>
            <input
              type="text"
              className="form-input"
              style={{ width: "100%", boxSizing: "border-box" }}
              id="psa-city"
            value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
            />
          </div>
          <div className="form-group" style={{ minWidth: 0, overflow: "hidden" }}>
            <label className="form-label" htmlFor="psa-state">State *</label>
            <input
              type="text"
              className="form-input"
              style={{ width: "100%", boxSizing: "border-box" }}
              id="psa-state"
            value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="ST"
              maxLength="2"
            />
          </div>
          <div className="form-group" style={{ minWidth: 0, overflow: "hidden" }}>
            <label className="form-label" htmlFor="psa-zip">ZIP *</label>
            <input
              type="text"
              className="form-input"
              style={{ width: "100%", boxSizing: "border-box" }}
              id="psa-zip"
            value={zip}
              onChange={(e) => setZip(e.target.value)}
              placeholder="12345"
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "1.5rem" }}>
          <button className="btn btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            Save Address
          </button>
        </div>
      </div>
    </div>
  );
}